// Generic group-by + date bucket helpers shared by PayrollPage (Piece 1
// Overview view) and anything else that needs to roll up rows into
// labeled buckets. Margin Report's existing groupByKey in utils/margin.js
// is functionally similar but hard-coded to revenue/cost/margin
// accumulators -- we leave that alone for now and might consolidate later.

// Bucket rows by an arbitrary key. The caller supplies:
//   - keyFn(row): a stable bucket key (null/undefined => row is skipped)
//   - makeBucket(key, row): initial accumulator object for a new bucket;
//     gets the first row that produced this key in case the bucket label
//     depends on row content (e.g. tech name).
//   - reduce(bucket, row): mutates the bucket in place to fold the row in.
//
// Returns an array of buckets in insertion order. Caller sorts.
export function groupBy(rows, keyFn, makeBucket, reduce) {
  const map = new Map();
  for (const row of rows) {
    const key = keyFn(row);
    if (key === null || key === undefined) continue;
    if (!map.has(key)) map.set(key, makeBucket(key, row));
    reduce(map.get(key), row);
  }
  return [...map.values()];
}

// ISO YYYY-MM-DD slice. Time-of-day stripped; bucket is local-time day.
export function dayKey(isoOrMs) {
  const d = toDate(isoOrMs);
  if (!d) return null;
  return localISO(d);
}

// Maps a startDay string to the JS Date.getDay() index (0=Sun..6=Sat).
// Accepts any day of the week so contractors with Thu-Wed or
// Fri-Thu pay weeks aren't locked into Mon/Sun.
export const DAY_TO_INDEX = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

function startDayIndex(startDay) {
  return DAY_TO_INDEX[startDay] ?? 1; // Monday fallback
}

// Week bucket key: returns the YYYY-MM-DD of the week's start date.
// startDay defaults to "monday" (ISO week convention) but any day name
// works -- a shop with a Thursday-Wednesday pay week passes "thursday"
// and weeks bucket from Thursday forward.
export function weekKey(isoOrMs, startDay = "monday") {
  const d = toDate(isoOrMs);
  if (!d) return null;
  const startIdx = startDayIndex(startDay);
  const offset = (d.getDay() - startIdx + 7) % 7;
  const weekStart = new Date(d);
  weekStart.setDate(d.getDate() - offset);
  weekStart.setHours(0, 0, 0, 0);
  return localISO(weekStart);
}

// YYYY-MM
export function monthKey(isoOrMs) {
  const d = toDate(isoOrMs);
  if (!d) return null;
  return localISO(d).slice(0, 7);
}

// YYYY-Q1..Q4
export function quarterKey(isoOrMs) {
  const d = toDate(isoOrMs);
  if (!d) return null;
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()}-Q${q}`;
}

// YYYY
export function yearKey(isoOrMs) {
  const d = toDate(isoOrMs);
  if (!d) return null;
  return String(d.getFullYear());
}

// Friendly labels for the date buckets. Caller controls locale.
export function labelForDateKey(bucketType, key) {
  if (!key) return "—";
  if (bucketType === "day") {
    const d = new Date(`${key}T00:00:00`);
    return Number.isNaN(d.getTime())
      ? key
      : d.toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        });
  }
  if (bucketType === "week") {
    const d = new Date(`${key}T00:00:00`);
    if (Number.isNaN(d.getTime())) return `Week of ${key}`;
    return `Week of ${d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}`;
  }
  if (bucketType === "month") {
    const [y, m] = key.split("-").map(Number);
    if (!y || !m) return key;
    const d = new Date(y, m - 1, 1);
    return d.toLocaleDateString(undefined, {
      month: "long",
      year: "numeric",
    });
  }
  if (bucketType === "quarter") return key;
  if (bucketType === "year") return key;
  return key;
}

function toDate(input) {
  if (input === null || input === undefined || input === "") return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function localISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Given an org's payPeriodConfig + a reference date (defaults to today),
// returns the active pay period's [start, end) boundaries as ms epoch
// values. Returns null if config is missing -- caller treats that as
// "no current pay period mode" and falls back to other date ranges.
//
// `end` is EXCLUSIVE -- midnight at the start of the day AFTER the
// last day of the period. Inclusive comparisons should use `< end`.
export function currentPayPeriodBounds(config, refDate = new Date()) {
  if (!config || !config.frequency) return null;
  const ref = new Date(refDate);
  ref.setHours(0, 0, 0, 0);

  if (config.frequency === "monthly") {
    const start = new Date(ref.getFullYear(), ref.getMonth(), 1);
    const end = new Date(ref.getFullYear(), ref.getMonth() + 1, 1);
    return {
      startMs: start.getTime(),
      endMs: end.getTime(),
      label: start.toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
      }),
    };
  }

  if (config.frequency === "weekly") {
    const startIdx = startDayIndex(config.startDayOfWeek);
    const offset = (ref.getDay() - startIdx + 7) % 7;
    const start = new Date(ref);
    start.setDate(ref.getDate() - offset);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 7);
    return {
      startMs: start.getTime(),
      endMs: end.getTime(),
      label: `Week of ${start.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}`,
    };
  }

  if (config.frequency === "biweekly") {
    // Anchor date defines a cycle start (any historical Mon/Sun). Find
    // the most recent cycle start <= ref by chunking 14-day windows
    // forward from the anchor.
    const anchorIso = config.anchorDate || "";
    if (!anchorIso) return null;
    const anchor = new Date(`${anchorIso}T00:00:00`);
    if (Number.isNaN(anchor.getTime())) return null;
    anchor.setHours(0, 0, 0, 0);
    const daysSinceAnchor = Math.floor(
      (ref.getTime() - anchor.getTime()) / 86400000
    );
    const cyclesSinceAnchor = Math.floor(daysSinceAnchor / 14);
    const start = new Date(anchor);
    start.setDate(anchor.getDate() + cyclesSinceAnchor * 14);
    const end = new Date(start);
    end.setDate(start.getDate() + 14);
    return {
      startMs: start.getTime(),
      endMs: end.getTime(),
      label: `${start.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })} – ${new Date(end.getTime() - 86400000).toLocaleDateString(
        undefined,
        { month: "short", day: "numeric", year: "numeric" }
      )}`,
    };
  }

  return null;
}
